import React from 'react';
import { Button, InputText } from '@insurancemenu/imenu-components-lib';
import SubtitleWithLines from 'components/common/SubtitleWithLines';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import FormsSpinner from 'components/common/FormSpinner';
import ErrorMessage from 'components/common/ErrorMessage';
import ActionModal from 'components/common/ActionModal';
import { useHistory } from 'react-router-dom';
import { getUrlParams } from 'global/hooks/getURLParams';
import { CODE_MESSAGE } from 'global/utils/codeMessages';
import { ModalType } from 'global/utils/ModalsHelper';
import { createStyles } from './styles';

const ResetPasswordForm = observer((): JSX.Element => {
  const classes = createStyles();
  const history = useHistory();
  const email = getUrlParams('email');
  const uuidCode = getUrlParams('uuid');
  // Mobx store
  const {
    resetPasswordStore: {
      resetPasswordForm,
      loading,
      errorCode,
      showPasswordRecoveryModal,
      resetPassword,
      setShowPasswordRecoveryModal,
    },
  } = useRootStore();

  const { password, repeatPassword } = resetPasswordForm.fields;

  // Form validations
  const passwordsMatch = password.value && repeatPassword.value ? password.value === repeatPassword.value : true;

  const isFormValid =
    Boolean(password.value && repeatPassword.value) && resetPasswordForm.isFormValid() && passwordsMatch;

  const onResetPassword = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (email && uuidCode) void resetPassword(email, uuidCode);
  };

  return (
    <form id="forgot-password-form" onSubmit={onResetPassword} className={classes.form}>
      <SubtitleWithLines text="Enter new strong password" className={classes.subtitle} />
      <div className={classes.formElements}>
        <InputText
          label="New Password"
          name="password"
          value={resetPasswordForm.fields.password.value}
          onChange={resetPasswordForm.onFieldChange}
          description="At least 8 characters"
          htmlType="password"
          className={classes.input}
          validationPassed={!password.unpassedValidationMsg}
          validationMessage={password.unpassedValidationMsg || ''}
        />
        <InputText
          label="Confirm Password"
          name="repeatPassword"
          value={resetPasswordForm.fields.repeatPassword.value}
          onChange={resetPasswordForm.onFieldChange}
          htmlType="password"
          className={classes.input}
          validationPassed={passwordsMatch}
          validationMessage={passwordsMatch ? '' : "Passwords don't match"}
        />
      </div>
      <div className={errorCode ? classes.buttonWrapperWithError : classes.validButtonWrapper}>
        {errorCode && <ErrorMessage error={CODE_MESSAGE[errorCode]} />}
        <Button
          htmlType="submit"
          type="contained"
          color="primary"
          size="large"
          className={classes.button}
          disabled={!isFormValid}
        >
          Reset Password
        </Button>
      </div>
      <ActionModal
        show={showPasswordRecoveryModal}
        title="Your password has been reset successfully!"
        subtitle="Now login with your new password."
        closeButtonText="Back to login"
        modalType={ModalType.SUCCESS}
        className={classes.passwordRecoveryModal}
        onClose={(): void => {
          setShowPasswordRecoveryModal(false);
          history.push('/login');
        }}
      />
      <FormsSpinner display={loading} />
    </form>
  );
});

export default ResetPasswordForm;
